import React, { useMemo } from 'react';
import { Button, type SelectOption } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { PhaseAccordion } from './PhaseAccordion';
import { DraftMiniGantt } from './DraftMiniGantt';
import type { UseProjectDraftReturn } from '../../hooks/useProjectDraft';

export interface CronogramaTabProps {
  draft: UseProjectDraftReturn;
}

/** `yyyy-mm-dd` → `dd/mm` (parse local). */
function fmtDate(d?: string): string {
  if (!d) return '—';
  const parsed = new Date(`${d}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Aba "Cronograma" — builder de fases em acordeões + painel lateral sticky com
 * resumo computado e preview do cronograma (`DraftMiniGantt`).
 */
export const CronogramaTab: React.FC<CronogramaTabProps> = ({ draft }) => {
  const { value } = draft;
  const { data: users } = useUsersDropdown();

  // Responsáveis elegíveis: líder + membros do projeto.
  const userOptions = useMemo<SelectOption[]>(() => {
    const eligible = new Set([value.leaderId, ...value.memberIds].filter(Boolean));
    return (users ?? [])
      .filter((u) => eligible.has(u.id))
      .map((u) => ({ value: u.id, label: u.name }));
  }, [users, value.leaderId, value.memberIds]);

  const usersById = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    (users ?? []).forEach((u) => map.set(u.id, { id: u.id, name: u.name }));
    return map;
  }, [users]);

  // Resumo computado.
  const summary = useMemo(() => {
    const dated = value.tasks.filter((t) => t.startDate && t.endDate);
    let period: string | null = null;
    if (dated.length > 0) {
      const start = dated.reduce((min, t) => (t.startDate < min ? t.startDate : min), dated[0]!.startDate);
      const end = dated.reduce((max, t) => (t.endDate > max ? t.endDate : max), dated[0]!.endDate);
      period = `${fmtDate(start)} – ${fmtDate(end)}`;
    }
    return {
      period,
      phases: value.phases.length,
      tasks: value.tasks.length,
      milestones: value.tasks.filter((t) => t.isMilestone).length,
      dependencies: value.tasks.reduce((sum, t) => sum + t.dependencies.length, 0),
      noAssignee: value.tasks.filter((t) => !t.assigneeId).length,
    };
  }, [value.tasks, value.phases]);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Coluna esquerda: builder de fases */}
        <div className="space-y-4">
          {value.phases.map((phase) => (
            <PhaseAccordion
              key={phase.id}
              phase={phase}
              phaseTasks={value.tasks.filter((t) => t.phaseId === phase.id)}
              allTasks={value.tasks}
              userOptions={userOptions}
              usersById={usersById}
              canRemove={value.phases.length > 1}
              onUpdatePhase={(patch) => draft.updatePhase(phase.id, patch)}
              onRemove={() => draft.removePhase(phase.id)}
              onAddTask={(task) => draft.addTask(phase.id, task)}
              onRemoveTask={draft.removeTask}
            />
          ))}

          <Button
            variant="secondary"
            icon="plus"
            onClick={draft.addPhase}
            data-testid="project-form-phase-add"
          >
            Nova fase
          </Button>
        </div>

        {/* Coluna direita: resumo + preview (sticky) */}
        <div className="lg:sticky lg:top-4 self-start space-y-4">
          <div
            className="rounded-lg border p-4 space-y-3"
            style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
            data-testid="project-form-summary"
          >
            <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
              Resumo
            </h3>
            <dl className="space-y-2 text-sm">
              <SummaryRow icon="calendar" label="Período" value={summary.period ?? 'Sem datas'} />
              <SummaryRow icon="list" label="Fases" value={String(summary.phases)} />
              <SummaryRow
                icon="check-circle"
                label="Tarefas"
                value={`${summary.tasks}${summary.milestones > 0 ? ` · ${summary.milestones} marco${summary.milestones > 1 ? 's' : ''}` : ''}`}
              />
              <SummaryRow icon="link" label="Dependências" value={String(summary.dependencies)} />
              {summary.noAssignee > 0 && (
                <SummaryRow
                  icon="user"
                  label="Sem responsável"
                  value={String(summary.noAssignee)}
                  warn
                />
              )}
            </dl>
          </div>

          <div
            className="rounded-lg border p-4 space-y-3"
            style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
          >
            <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
              Prévia do cronograma
            </h3>
            <DraftMiniGantt phases={value.phases} tasks={value.tasks} />
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryRow: React.FC<{
  icon: 'calendar' | 'list' | 'check-circle' | 'link' | 'user';
  label: string;
  value: string;
  warn?: boolean;
}> = ({ icon, label, value, warn }) => (
  <div className="flex items-center justify-between gap-2">
    <dt className="flex items-center gap-1.5" style={{ color: 'var(--theme-muted-text)' }}>
      <Icon name={icon} className="w-3.5 h-3.5" />
      {label}
    </dt>
    <dd
      className="font-medium text-right"
      style={{ color: warn ? 'var(--theme-warning-color)' : 'var(--theme-card-text)' }}
    >
      {value}
    </dd>
  </div>
);

CronogramaTab.displayName = 'CronogramaTab';
